import {
  getProductLinkDetails,
  getQuickCheckoutDetails,
} from "./../services/actionServices";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { useAppSelector } from "store/hooks";

import { getActionCount, getActionList } from "store/services/actionServices";
import { RootState } from "store/store";
import { getEffectiveAgreementStatus } from "utils/agreementExpiration";

type ActionListDataTypes = Root2[];

interface Root2 {
  id: number;
  sourceStatus: string;
  CompletedOn: any;
  total_amount: number;
  accounts_id: number;
  due: string;
  insert_date: string;
  expiry_date: string;
  next_billing_date: any;
  start_date: string;
  url_slug: string | null;
  action: string;
  TotalCount: number;
}

interface ActionListTypes {
  totalCount: number;
  actionListData: ActionListDataTypes;
}

interface ActionCountTypes {
  paymentLink: PaymentLink[];
  agreement: Agreement;
  invoice: Invoice;
  quote: Quotes;
  summary: {
    outstanding: number;
    completed: number;
    expired: number;
    canceled: number;
    declined: number;
    all: number;
  };
}

interface Invoice {
  OPENED: number;
  PAID: number;
  OVERDUE: number;
  CANCELLED: number;
}

interface Quotes {
  accepted: number;
  open: number;
}

interface PaymentLink {
  active: number;
  expired: number;
  completed: number;
}

interface Agreement {
  Replaced: number;
  Expired: number;
  Accepted: number;
  Completed: number;
  Cancelled: number;
  Pending: number;
}
interface InitialState {
  actionCount: ActionCountTypes;
  actionList: ActionListTypes;
  isTableLoading: boolean;
  previewType:
    | "payment_link"
    | "agreement"
    | "invoice"
    | "quotes"
    | "payment"
    | "subscription"
    | "";
  previewId: any;
}

const initialState: InitialState = {
  actionCount: {} as ActionCountTypes,
  actionList: {} as ActionListTypes,
  isTableLoading: false,
  previewType: "",
  previewId: null,
};

export const ActionCountGet = createAsyncThunk(
  "action-count/get",
  async (params: any) => {
    return await getActionCount(params);
  }
);

export const actionListGet = createAsyncThunk(
  "action-list/get",
  async (payload: any) => {
    return await getActionList(payload);
  }
);

export const productLinkGet = createAsyncThunk(
  "product-link-details/get",
  async (params: any) => {
    return await getProductLinkDetails(params);
  }
);

export const quickCheckoutGet = createAsyncThunk(
  "quick-checkout-details/get",
  async (params: { slug: string }) => {
    return await getQuickCheckoutDetails(params);
  }
);

const actionSlice = createSlice({
  name: "actionCenter",
  initialState,
  reducers: {
    // Named for this slice: it was previously called clearPaymentsSlice, which
    // both misdescribed it and collided with the payments slice's own action.
    clearActionSlice: (state: any) => {
      for (const key in initialState) {
        if (initialState.hasOwnProperty(key)) {
          const typedKey = key as keyof typeof initialState;
          state[typedKey] = initialState[typedKey];
        }
      }
    },
    showPreview: (state, action) => {
      state.previewType = action.payload.previewType;
      state.previewId = action.payload.previewId;
    },
    setPreviewType: (state, action) => {
      state.previewType = action.payload;
    },
    setPreviewId: (state, action) => {
      state.previewId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      ActionCountGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        const data = action.payload?.body?.data;
        if (data) {
          const { paymentLink, agreement, invoice, quote } = data?.result;
          // Agreement
          let AGopen = agreement?.Opened ? agreement?.Opened : 0;
          let AGaccepted = agreement?.Accepted ? agreement.Accepted : 0;
          let AGcomp = agreement?.Completed ? agreement?.Completed : 0;
          let AGexp = agreement?.Expired ? agreement?.Expired : 0;
          let AGcanceled = agreement?.Cancelled ? agreement?.Cancelled : 0;
          let AGPending = agreement?.Pending ? agreement?.Pending : 0;
          // Payment Link
          let PLcomp2 = paymentLink[0]?.completed
            ? paymentLink[0]?.completed
            : 0;
          let PLexp2 = paymentLink[0]?.expired ? paymentLink[0]?.expired : 0;
          let PLactive = paymentLink[0]?.active ? paymentLink[0]?.active : 0;

          // INVOICE
          let INopen = invoice?.OPEN ? invoice?.OPEN : 0;
          let INpending = invoice?.PENDING ? invoice?.PENDING : 0;
          let INpaid = invoice?.PAID ? invoice?.PAID : 0;
          let INover_due = invoice?.OVERDUE ? invoice?.OVERDUE : 0;
          let INcanceled = invoice?.CANCELLED ? invoice?.CANCELLED : 0;
          // let INUC = invoice?.UC ? invoice?.UC : 0;

          // Quotes
          let QUsent = quote?.sent ? quote?.sent : 0;
          let QUopen = quote?.open ? quote?.open : 0;
          let QUaccepted = quote?.accepted ? quote?.accepted : 0;
          let QUexpired = quote?.expired ? quote?.expired : 0;
          let QUCanceled = (quote?.cancelled ? quote?.cancelled : 0);
          let QUDeclined = quote?.declined ? quote?.declined : 0;

          const outstanding =
            PLactive +
            AGopen +
            INopen +
            INover_due +
            INpending +
            QUopen +
            AGPending +
            QUsent;

          const completed =
            PLcomp2 + AGcomp + AGaccepted + INpaid + QUaccepted + QUDeclined;
          const expired = PLexp2 + AGexp + QUexpired;
          const canceled = AGcanceled + INcanceled + QUCanceled;
          const all = outstanding + completed + expired + canceled;
          
          state.actionCount = {
            ...data,
            summary: {
              outstanding,
              completed,
              expired,
              canceled,
              all,
            },
          };
        }
      }
    );
    builder.addCase(
      actionListGet.pending,
      (state, action: PayloadAction<any>) => {
        state.actionList = {
          ...state.actionList,
          actionListData: [],
        };
        state.isTableLoading = true;
      }
    );
    builder.addCase(
      actionListGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        const data = action.payload?.body?.data || {};
        state.actionList = {
          ...data,
          actionListData: (data?.actionListData || []).map((item: Root2) =>
            item?.action === "agreement"
              ? {
                  ...item,
                  sourceStatus: getEffectiveAgreementStatus(
                    item?.sourceStatus,
                    item?.expiry_date
                  ),
                }
              : item
          ),
        };
        state.isTableLoading = false;
      }
    );
  },
});

const { clearActionSlice, showPreview, setPreviewType, setPreviewId } =
  actionSlice.actions;
export { clearActionSlice, showPreview, setPreviewType, setPreviewId };
export default actionSlice.reducer;

const selectActionCount = (state: RootState) => state.action.actionCount;
export const useActionCount = () => {
  const actionCount = useAppSelector(selectActionCount);
  return useMemo(() => actionCount, [actionCount]);
};

const selectPaymentHistory = (state: RootState) =>
  state.action.actionList;
export const useActionList = () => {
  const actionList = useAppSelector(selectPaymentHistory);
  return useMemo(() => actionList, [actionList]);
};

const selectDataLoading = (state: RootState) =>
  state.action.isTableLoading;
export const useIsTableLoading = () => {
  const isTableLoading = useAppSelector(selectDataLoading);
  return useMemo(() => isTableLoading, [isTableLoading]);
};
